// FLEET LIVE's motion bookkeeping (#6778): what changed between two stream patches, as
// timed effects the painters animate. VISUAL ONLY: no number on the page is derived from
// it, and a dropped frame or a reconnect re-snapshot loses nothing but an animation.
//
// The prototype's simulation emitted these as it advanced its own items (`emit`,
// `:3387`); the build derives them from the real data instead: an item whose stage moved
// flashes its new stage gate, an item whose CI fix rounds grew draws the dashed return arc
// and its red tint, an item reaching "Shipped" bursts, and an item that left the set fades.
// Under reduced motion none of this runs (the container never calls `noteItems`), so a
// still frame carries no effect.

import type { LiveItem } from './fleet-live';

/** How long each effect lives, in ms (prototype `:3906`, `:3913`, `:3916`, `:3934`). */
export const MOTION_MS = {
  gate: 700,
  arc: 1600,
  tint: 1200,
  fade: 1800,
  topoFade: 900,
  flash: 900,
} as const;

export type MotionEffect = {
  /** Unique per effect, so each painter starts a burst once. */
  readonly seq: number;
  readonly kind: 'gate' | 'ci' | 'ship';
  readonly id: string;
  readonly stage: number;
  readonly at: number;
};

export type GoneItem = { readonly item: LiveItem; readonly at: number };

export type Motion = {
  readonly effects: readonly MotionEffect[];
  readonly gone: readonly GoneItem[];
  /** When each item's latest CI retry began. */
  readonly ciAt: ReadonlyMap<string, number>;
};

export type MotionTracker = {
  /** Fold one patch's items in at `now`; the first call only records them. */
  readonly noteItems: (items: readonly LiveItem[], now: number) => void;
  /** What is still animating at `now` (older effects are pruned). */
  readonly at: (now: number) => Motion;
  /** True while some effect is still running. */
  readonly busy: (now: number) => boolean;
};

const SHIPPED = 7;
const LONGEST = MOTION_MS.fade;

export function createMotionTracker(): MotionTracker {
  let prev: Map<string, LiveItem> | null = null;
  let effects: MotionEffect[] = [];
  let gone: GoneItem[] = [];
  const ciAt = new Map<string, number>();
  let seq = 0;

  const prune = (now: number): void => {
    effects = effects.filter((e) => now - e.at < LONGEST);
    gone = gone.filter((g) => now - g.at < LONGEST);
    for (const [id, at] of ciAt) if (now - at >= MOTION_MS.arc) ciAt.delete(id);
  };

  const push = (kind: MotionEffect['kind'], item: LiveItem, now: number): void => {
    seq += 1;
    effects.push({ seq, kind, id: item.id, stage: item.stage, at: now });
  };

  const noteMove = (was: LiveItem | undefined, item: LiveItem, now: number): void => {
    if (was === undefined) return;
    if (item.stage > was.stage) push(item.stage === SHIPPED ? 'ship' : 'gate', item, now);
    if ((item.ciRounds ?? 0) > (was.ciRounds ?? 0)) {
      push('ci', item, now);
      ciAt.set(item.id, now);
    }
  };

  return {
    noteItems(items, now) {
      const next = new Map(items.map((i) => [i.id, i]));
      if (prev !== null) {
        const before = prev;
        for (const item of items) noteMove(before.get(item.id), item, now);
        for (const [id, item] of before) if (!next.has(id)) gone.push({ item, at: now });
      }
      prev = next;
      prune(now);
    },
    at(now) {
      prune(now);
      return { effects, gone, ciAt };
    },
    busy(now) {
      prune(now);
      return effects.length > 0 || gone.length > 0;
    },
  };
}

/** One spark of a burst (prototype `burst`, `:3368`). */
export type Particle = { x: number; y: number; vx: number; vy: number; life: number; s: number };

/** `n` sparks from a point. `rand` is injected so a test can pin them. */
export function burst(
  x: number,
  y: number,
  n: number,
  rand: () => number = Math.random,
): Particle[] {
  return Array.from({ length: n }, () => {
    const a = rand() * Math.PI * 2;
    const v = 0.02 + rand() * 0.07;
    return { x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 0.03, life: 1, s: 1 + rand() * 1.4 };
  });
}

/** Advance sparks by `dt` ms, dropping the spent ones (prototype drawParts, `:3369`). */
export function stepParticles(list: Particle[], dt: number): Particle[] {
  return list.filter((p) => {
    p.life -= dt / 900;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    return p.life > 0;
  });
}
